import { IMerchGroup } from '@models/MerchGroup/i-merch-group';
import { ApolloClient } from '@apollo/client';
import SHOP_GROUP_BY_SLUG_QUERY from '../graphql/shop-group-by-slug.graphql';
import TOP_LEVEL_SHOP_GROUPS from '../graphql/top-level-shop-groups.graphql';
import { IMerchGroupCategory } from './MerchGroupCategory';

export type IShopGroup = IMerchGroupCategory | IMerchGroup;

export enum ShopGroupType {
  MERCH_GROUP = 'MerchGroupModel',
  MERCH_GROUP_CATEGORY = 'MerchGroupCategory'
}

export async function getShopGroupBySlug(
  client: ApolloClient<any>,
  slug: string
): Promise<any | undefined> {
  const [lastSlug] = slug.split('/').slice(-1);
  const response = await client.query({
    query: SHOP_GROUP_BY_SLUG_QUERY,
    // this is needed because it tries to do heuristic fragment matching
    fetchPolicy: 'no-cache',
    variables: {
      slug: lastSlug
    }
  });

  return response?.data?.getShopGroupBySlug;
}

export async function getTopLevelShopGroups(
  client: ApolloClient<any>
): Promise<IMerchGroupCategory[] | undefined> {
  const response = await client.query({
    query: TOP_LEVEL_SHOP_GROUPS,
  });

  return response?.data?.getTopLevelMerchGroupCategories;
}

/* eslint-disable no-param-reassign */
export function setSelectedShopGroup(
  shopGroups: IShopGroup[] | undefined,
  selectedShopGroup: IShopGroup | undefined
): IShopGroup[] | undefined {
  if (!shopGroups || !(selectedShopGroup?.id)) return shopGroups;

  return [...shopGroups].map((shopGroup : any) => {
    if (shopGroup.id === selectedShopGroup.id &&
        (selectedShopGroup as any).__typename === shopGroup.__typename) {
      shopGroup.selected = true;
    } else {
      shopGroup.selected = false;
    }
    if (shopGroup?.categoryChildren?.length) {
      shopGroup.categoryChildren =
        setSelectedShopGroup(shopGroup.categoryChildren, selectedShopGroup);
    } else if (shopGroup?.children?.length) {
      shopGroup.children = setSelectedShopGroup(shopGroup.children, selectedShopGroup);
    }
    return shopGroup;
  });
}

export function getSelectedShopGroupChain(
  shopGroups: IMerchGroupCategory[] | undefined
): IShopGroup[] | undefined {
  if (!shopGroups) return undefined;

  const selectedShopGroup = shopGroups.find((shopGroup) => shopGroup.selected);
  if (selectedShopGroup) {
    return [selectedShopGroup];
  } else {
    for (let i = 0; i < shopGroups.length; i += 1) {
      const shopGroup = shopGroups[i];
      if (shopGroup?.children) {
        const subChain = getSelectedShopGroupChain(shopGroup.children);
        if (subChain) {
          return [shopGroup, ...subChain];
        }
      }
    }
  }
  return undefined;
}

export function isChildSelected(category: IMerchGroupCategory): boolean {
  return !!category.children && !!category.children.find((child) => {
    return child.selected || isChildSelected(child);
  });
}
